import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import Heading from 'LuxKit/Typography/Heading'
import { useContext } from 'react'
import { useIntl } from 'react-intl'
import CurrencyContext from 'contexts/currencyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import type { DrawerHotelPrice } from './types'

interface Props {
  price?: DrawerHotelPrice
  luxPlusPrice?: number
  value?: number
  discountPercent?: number
  memberDiscountPercent?: number
  offerId: string
  deeplinkUrl?: string
  saleUnit?: string
  isBooked?: boolean
  orderId?: string
}

function HotelDetailPricing({
  price,
  luxPlusPrice,
  value,
  discountPercent,
  memberDiscountPercent,
  offerId,
  deeplinkUrl,
  saleUnit,
  isBooked,
  orderId,
}: Props) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const showLuxPlusPricing = luxPlusEnabled && !!luxPlusPrice && !!price && luxPlusPrice < price.total
  const bookNowHref = deeplinkUrl || `/offer/${offerId}`

  const bookNowLabel = intl.formatMessage({
    id: 'jPThW3ju3Ar48FaMK2Va',
    defaultMessage: 'Book now',
    description: 'Call-to-action button text to book the selected hotel offer on the offer page',
  })

  const bookingDetailsLabel = intl.formatMessage({
    id: 'V3if7yCh9Y75j9TcYdbF',
    defaultMessage: 'Booking details',
    description: 'CTA button text to view booking details in trip planner',
  })

  if (isBooked) {
    return (
      <Group direction="horizontal" horizontalAlign="end">
        <TextButton
          kind="tertiary"
          size="large"
          href={orderId ? `/account/my-escapes/order/${orderId}` : '/account/my-escapes'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {bookingDetailsLabel}
        </TextButton>
      </Group>
    )
  }

  if (!price) {
    return (
      <Group direction="vertical" gap={16}>
        <Heading variant="heading4">
          {intl.formatMessage({
            id: 'haM13lTwzMAV8P10PNiJ',
            defaultMessage: 'Pricing',
            description: 'Heading for hotel pricing section on the hotel or room booking page',
          })}
        </Heading>
        <Group
          direction="vertical"
          tabletDirection="horizontal"
          gap={16}
          tabletHorizontalAlign="space-between"
          tabletVerticalAlign="center"
        >
          <TextLink
            size="medium"
            href={bookNowHref}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<LineExternalLinkAltIcon />}
          >
            {intl.formatMessage({
              id: 'wBc1bOKcrj5AocujDj03',
              defaultMessage: 'View on Luxury Escapes for pricing details',
              description: 'Link to view hotel pricing on Luxury Escapes website',
            })}
          </TextLink>
          <TextButton
            kind="primary"
            size="large"
            endIcon={<LineExternalLinkAltIcon />}
            href={bookNowHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            {bookNowLabel}
          </TextButton>
        </Group>
      </Group>
    )
  }

  const priceLabel = intl.formatMessage(
    {
      id: 'TmpthJ75SbMB0p3uodV5',
      defaultMessage: '{count, plural, one {# night} other {# nights}} from',
      description: 'Hotel pricing caption showing number of nights in the trip planner AI chat',
    },
    { count: price.nights },
  )
  const hasValue = value !== undefined && value > 0 && value >= price.total
  const effectiveDiscountPercent = showLuxPlusPricing && canViewLuxPlusBenefits && memberDiscountPercent != null
    ? memberDiscountPercent
    : discountPercent

  return (
    <Group
      direction="vertical"
      tabletDirection="horizontal"
      gap={16}
      tabletHorizontalAlign="space-between"
      tabletVerticalAlign="end"
    >
      <PriceDetails
        price={price.total}
        luxPlusPrice={luxPlusPrice}
        currencyCode={price.currency ?? currentCurrency}
        priceLabel={priceLabel}
        totalLabel={saleUnit ?? 'total'}
        value={hasValue ? value : undefined}
        discount={effectiveDiscountPercent}
        discountDisplayType="following"
        showTaxesAndFees={false}
      />
      <TextButton
        kind="primary"
        size="large"
        endIcon={<LineExternalLinkAltIcon />}
        href={bookNowHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        {bookNowLabel}
      </TextButton>
    </Group>
  )
}

export default HotelDetailPricing
